/**
 * Permissions master-detail (full-admin only).
 *
 * Mirrors RolesView: the grantable targets on the left (the admin tier + each
 * admin endpoint), the selected target's current grants on the right. A grant
 * binds a principal (a user or a role) to a relation on the target resource.
 *
 * Same surface language as the rest of the Organisation console — hairline
 * Panels, one Action-Blue accent, the four mandatory async states, and
 * server-authoritative writes (no optimism; the row changes only once the
 * server confirms).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAdminUsers } from '@/hooks/useAdminAnalytics';
import { StaleDataNotice } from '@/components/clinical';
import {
  EmptyState,
  ErrorState,
  LoadingRows,
  Panel,
  RemovableRow,
  SectionHeader,
  errMessage,
} from './OrgUi';
import { ExpiryChip } from '@/components/access/atoms';
import { GrantPermissionDialog } from './PermissionsDialogs';
import { usePermissionTargets, usePermissionGrants, useRevokePermission } from './usePermissions';
import { useRoles, indexRolesById, indexUsersById } from './useOrg';
import { RELATION_ADMIN } from '@/api/endpoints/admin';
import type {
  PermissionGrant,
  PermissionGrantBody,
  PermissionTargetsResponse,
} from '@/api/endpoints/admin';

/** Which section of the target list a row belongs to. */
type TargetGroup = 'platform' | 'endpoint' | 'agent';

/** A target the admin can manage grants on. */
interface Target {
  /** Stable list key (resource_type:resource_id). */
  key: string;
  resourceType: string;
  resourceId: string;
  name: string;
  /** The relations grantable on this target — model-derived, per family. */
  relations: string[];
  /** Distinguishes the single admin-tier row from the endpoint rows. */
  isAdminTier: boolean;
  group: TargetGroup;
  /** Optional second line explaining the row (an agent's own gate). */
  hint?: string;
}

/**
 * Fold the targets vocabulary into one ordered list, grouped for the sidebar.
 *
 * The platform singleton leads (highest privilege), then the admin areas, then
 * the agents. The platform row carries the admin TIERS plus the other delegable
 * platform authorities (approver, contact_viewer) in ONE row: they are all
 * relations on the same `system:platform` object, so splitting them into two
 * rows would show the same grant list twice and let an admin grant from either
 * — the same target wearing two faces.
 *
 * The admin_object arrives as "type:id" (e.g. "system:platform") — split on the
 * FIRST colon so a future id containing a colon still resolves.
 */
function buildTargets(data: PermissionTargetsResponse, t: TFunction): Target[] {
  const [adminType, ...adminRest] = data.admin_object.split(':');
  const adminId = adminRest.join(':');
  const platform: Target = {
    key: data.admin_object,
    resourceType: adminType,
    resourceId: adminId,
    name: t('org.permissions.adminTier', 'Admin access (platform)'),
    relations: [...data.admin_relations, ...(data.platform_relations ?? [])],
    isAdminTier: true,
    group: 'platform',
  };
  const endpoints: Target[] = data.endpoints.map((e) => ({
    key: `endpoint:${e.id}`,
    resourceType: 'endpoint',
    resourceId: e.id,
    name: e.name,
    relations: data.endpoint_relations,
    isAdminTier: false,
    group: 'endpoint',
    // Areas differ in what they expose: query logs and error reports carry raw
    // cross-user, user-identifying rows; query clusters are aggregates. Say so,
    // so handing one to a whole role is an informed choice.
    hint: e.sensitive
      ? t('org.permissions.areaSensitive', 'Cross-user, user-identifying content')
      : undefined,
  }));
  // Absent on a backend older than the agent-permissions release — render the
  // families we did receive rather than crashing on a missing key.
  const agentRelations = data.agent_relations ?? [];
  const agents: Target[] = (data.agents ?? []).map((a) => ({
    key: `agent:${a.id}`,
    resourceType: 'agent',
    resourceId: a.id,
    name: a.name,
    relations: agentRelations,
    isAdminTier: false,
    group: 'agent',
    // State the agent's CURRENT reach, so granting is an informed widening:
    // a restricted agent opens only for whoever is granted it here; an open one
    // is already available to everyone and a grant on it changes nothing. An
    // api-service that predates the flag says neither, and so do we — during a
    // rolling deploy a confident "open to everyone" on a restricted agent is
    // worse than no hint at all.
    hint:
      a.restricted === undefined
        ? undefined
        : a.restricted
          ? t('org.permissions.agentRestricted', 'Restricted — opens only for who is granted it here')
          : t('org.permissions.agentOpen', 'Open to everyone — a grant adds nothing'),
  }));
  return [platform, ...endpoints, ...agents];
}

/** Section heading copy per group, in the order the sidebar renders them. */
const TARGET_GROUPS: { group: TargetGroup; key: string; fallback: string }[] = [
  { group: 'platform', key: 'org.permissions.groupPlatform', fallback: 'Platform' },
  { group: 'endpoint', key: 'org.permissions.groupAreas', fallback: 'Admin areas' },
  { group: 'agent', key: 'org.permissions.groupAgents', fallback: 'Agents' },
];

export function PermissionsView() {
  const { t } = useTranslation();
  const { data, isLoading, isError, isFetching, error, refetch } = usePermissionTargets();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const targets = useMemo(() => (data ? buildTargets(data, t) : []), [data, t]);
  const byKey = useMemo(() => {
    const m = new Map<string, Target>();
    for (const t of targets) m.set(t.key, t);
    return m;
  }, [targets]);

  // If the vocabulary changes out from under a selection, drop it.
  useEffect(() => {
    if (selectedKey && targets.length > 0 && !byKey.has(selectedKey)) setSelectedKey(null);
  }, [targets, byKey, selectedKey]);

  const selected = selectedKey ? byKey.get(selectedKey) ?? null : null;

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(260px,320px)_1fr]">
      <Panel className="flex max-h-[70vh] min-h-[420px] flex-col p-3">
        <div className="mb-2 px-1">
          <h2 className="text-sm font-semibold text-neutral-900">{t('org.permissions.targets', 'Targets')}</h2>
          <p className="text-xs text-neutral-400">
            {t(
              'org.permissions.targetsHint',
              'Platform authority, every admin area, and every agent you can grant.',
            )}
          </p>
        </div>
        <div className="-mx-1 flex-1 overflow-y-auto px-1">
          {/* A background refetch failing (or in flight) with a cached
              target list still showing must stay visible. */}
          {data && <StaleDataNotice isFetching={isFetching} isError={isError} className="mb-2" />}
          {isError && !data ? (
            <ErrorState error={error} onRetry={() => void refetch()} />
          ) : isLoading ? (
            <LoadingRows rows={5} />
          ) : targets.length === 0 ? (
            <EmptyState
              title={t('org.permissions.noTargets', 'No grantable targets')}
              hint={t(
                'org.permissions.noTargetsHint',
                'The platform exposes no admin areas to grant. This usually means the authz model has not been configured.',
              )}
            />
          ) : (
            <div className="space-y-3">
              {TARGET_GROUPS.map(({ group, key, fallback }) => {
                const rows = targets.filter((target) => target.group === group);
                if (rows.length === 0) return null;
                return (
                  <div key={group}>
                    <div className="px-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-neutral-400">
                      {t(key, fallback)}
                    </div>
                    <ul className="space-y-0.5">
                      {rows.map((target) => (
                        <li key={target.key}>
                          <button
                            onClick={() => setSelectedKey(target.key)}
                            className={cn(
                              'w-full rounded-lg px-3 py-2 text-left transition-colors',
                              selectedKey === target.key ? 'bg-primary/10' : 'hover:bg-neutral-50',
                            )}
                          >
                            <div
                              className={cn(
                                'truncate text-sm',
                                selectedKey === target.key
                                  ? 'font-medium text-primary'
                                  : 'text-neutral-800',
                              )}
                            >
                              {target.name}
                            </div>
                            <div className="truncate font-mono text-[11px] text-neutral-400">
                              {target.resourceType}:{target.resourceId}
                            </div>
                            {target.hint && (
                              <div className="truncate text-[11px] text-neutral-400">
                                {target.hint}
                              </div>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Panel>

      <Panel className="min-h-[420px] p-6">
        {selected ? (
          <TargetDetail key={selected.key} target={selected} />
        ) : (
          <div className="flex h-full items-center justify-center">
            <EmptyState
              title={t('org.permissions.selectTarget', 'Select a target')}
              hint={t(
                'org.permissions.selectTargetHint',
                'Pick a platform authority, an admin area or an agent on the left to see who can reach it and grant access.',
              )}
            />
          </div>
        )}
      </Panel>
    </div>
  );
}

function TargetDetail({ target }: { target: Target }) {
  const { t } = useTranslation();
  const [granting, setGranting] = useState(false);
  const grants = usePermissionGrants(target.resourceType, target.resourceId);
  const { data: users } = useAdminUsers();
  const { data: roles } = useRoles();
  const revoke = useRevokePermission();
  const [pending, setPending] = useState<string | null>(null);

  const userById = useMemo(() => indexUsersById(users), [users]);
  const roleById = useMemo(() => indexRolesById(roles), [roles]);

  const grantKey = (g: PermissionGrant) => `${g.principal_type}:${g.principal_id}:${g.relation}`;

  // Resolve a principal id to a human name (a user's "Name (email)" or a role's
  // name); fall back to the bare id when the directory has not loaded it.
  const principalLabel = (g: PermissionGrant): string => {
    if (g.principal_type === 'role') return roleById[g.principal_id]?.name ?? g.principal_id;
    return userById[g.principal_id] ?? g.principal_id;
  };

  const rows = grants.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold text-neutral-900">{target.name}</h2>
          <p className="mt-1 font-mono text-xs text-neutral-500">
            {target.resourceType}:{target.resourceId}
          </p>
        </div>
        <Button size="sm" onClick={() => setGranting(true)} disabled={target.relations.length === 0}>
          {t('org.permissions.grantAccess', 'Grant access')}
        </Button>
      </div>

      <Panel className="p-4">
        <SectionHeader title={t('org.permissions.whoCanReach', 'Who can reach this')} count={rows.length} />
        <div className="mt-3">
          {/* Same visibility rule as the targets list above. */}
          {grants.data && <StaleDataNotice isFetching={grants.isFetching} isError={grants.isError} className="mb-2" />}
          {grants.isError && !grants.data ? (
            <ErrorState error={grants.error} onRetry={() => void grants.refetch()} />
          ) : grants.isLoading ? (
            <LoadingRows />
          ) : rows.length === 0 ? (
            <EmptyState
              title={t('org.permissions.noGrants', 'No one has been granted this')}
              hint={t('org.permissions.noGrantsHint', "Grant a person or a role one of this target's relations.")}
              action={
                <Button size="sm" variant="outline" onClick={() => setGranting(true)} disabled={target.relations.length === 0}>
                  {t('org.permissions.grantAccess', 'Grant access')}
                </Button>
              }
            />
          ) : (
            <div className="space-y-1">
              {rows.map((g) => (
                <RemovableRow
                  key={grantKey(g)}
                  removing={pending === grantKey(g)}
                  removeLabel={t('org.permissions.revoke', 'Revoke')}
                  onRemove={() => {
                    // Full-admin relation on the admin tier: require an explicit
                    // confirmation to prevent accidental lockout. Non-admin
                    // relations (admin_viewer, viewer, etc.) revoke without prompt.
                    if (target.isAdminTier && g.relation === RELATION_ADMIN) {
                      const label = principalLabel(g);
                      const confirmed = window.confirm(
                        t(
                          'org.permissions.revokeAdminConfirm',
                          'Remove full platform-admin access for "{{label}}"?\n\nRevoking the last admin will lock the organisation out of admin UI. Only proceed if another admin remains.',
                          { label },
                        ),
                      );
                      if (!confirmed) return;
                    }
                    setPending(grantKey(g));
                    const body: PermissionGrantBody = {
                      resource_type: target.resourceType,
                      resource_id: target.resourceId,
                      principal_type: g.principal_type,
                      principal_id: g.principal_id,
                      relation: g.relation,
                    };
                    revoke.mutate(body, { onSettled: () => setPending(null) });
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-neutral-500">
                      {g.principal_type}
                    </span>
                    <span className="truncate text-sm text-neutral-800">{principalLabel(g)}</span>
                    <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                      {g.relation}
                    </span>
                    {/*
                      A conditioned grant lapses on its own. Without this the
                      30-day new-user admin-view trial is a row identical to a
                      deliberate delegation, so a user inside their trial reads
                      as holding two standing grants. Same chip the access
                      surfaces use, so "expires in 12 days" means one thing
                      platform-wide.
                    */}
                    {g.expires_at && <ExpiryChip expiresAt={g.expires_at} />}
                  </div>
                </RemovableRow>
              ))}
            </div>
          )}
          {revoke.isError && <p className="mt-2 text-xs text-red-600">{errMessage(revoke.error)}</p>}
        </div>
      </Panel>

      <GrantPermissionDialog
        open={granting}
        onOpenChange={setGranting}
        resourceType={target.resourceType}
        resourceId={target.resourceId}
        targetName={target.name}
        relations={target.relations}
      />
    </div>
  );
}
